import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http'
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { PreloadAllModules, RouterModule } from '@angular/router'
import {
  ConfigService,
  CoreModule,
  I18nService,
  MomentTimeService,
} from '@wm-front/core'
import {
  FeatureFlagModule,
  MonitoringModule,
  provideChunkLoadRecovery,
  provideNewThemeFlag,
} from '@wm-front/core-tools'
import { ENVIRONMENT } from '@wm-front/shared/environment'
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core'
import { firstValueFrom } from 'rxjs'
import { ClientDataModule } from '@wm-front/client/data'
import { environment } from '../environments/environment'
import { GraphQLModule } from '@wm-front/shared/modules'
import { NgxsModule } from '@ngxs/store'
import { AuthState } from '@wm-front/client/auth/data-access'
import { withNgxsResetPlugin } from 'ngxs-reset-plugin'
import { provideNgxsDataPlugin } from '@angular-ru/ngxs'
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay'
import {
  SimpleFilterExternalState,
  SimpleFilterState,
} from '@wm-front/shared/simple-filters'

export const appConfig: ApplicationConfig = {
  providers: [
    I18nService,
    { provide: ENVIRONMENT, useValue: environment },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: {
        usePopover: false,
      },
    },
    provideAppInitializer(async () => {
      const initializerFn = (
        (
          configService: ConfigService,
          i18nService: I18nService,
          momentTimeService: MomentTimeService
        ) =>
        async (): Promise<void> =>
          firstValueFrom(
            configService.loadAppConfig('wm-front', environment.production)
          ).then((config) => {
            /** We would inject custom logic here to initialize all our custom providers */
            i18nService.init(config.defaultLanguage, config.supportedLanguages)
            momentTimeService.init()
          })
      )(inject(ConfigService), inject(I18nService), inject(MomentTimeService))
      return initializerFn()
    }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    withNgxsResetPlugin(),
    provideNgxsDataPlugin(),
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      CoreModule,
      GraphQLModule.forRoot({
        graphqlUrl: environment.graphqlUrl,
        appName: environment.appName,
      }),
      MonitoringModule.forRoot(environment.datadog ?? {}),
      FeatureFlagModule.forRoot(environment.launchDarkly),
      NgxPageScrollCoreModule,
      NgxsModule.forRoot(
        [AuthState, SimpleFilterState, SimpleFilterExternalState],
        {
          developmentMode: !environment.production,
          selectorOptions: { injectContainerState: true },
        }
      ),
      ClientDataModule,
      RouterModule.forRoot(
        [
          {
            path: 'auth',
            loadChildren: async () =>
              (await import('@wm-front/client/auth/feature')).routes,
          },
          {
            path: 'platform',
            loadChildren: async () =>
              (await import('@wm-front/client/container/feature')).routes,
          },
          {
            path: '',
            redirectTo: 'auth',
            pathMatch: 'full',
          },
          {
            path: '**',
            redirectTo: 'auth',
          },
        ],
        {
          enableTracing: false,
          scrollPositionRestoration: 'enabled',
          preloadingStrategy: PreloadAllModules,
        }
      )
    ),
    provideChunkLoadRecovery(),
    provideNewThemeFlag(),
  ],
}
